import React, { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import withPreview from "../utility/with-preview";
import { Link } from "./link";
import safeSlugify from "../utility/safe-slugify";
import { normalizeRelativePath } from "../utility/paths";
import SearchIcon from "../icons/cerca.svg";

const FaqPage = ({ data }) => {
  const faqperpage = useMemo(() => Number(data.faqperpage || 10), [data.faqperpage]);
  const [text, setText] = useState("");
  const onBlur = useCallback(() => 0, []);
  const { faqPage, allFaq, labelManagerLabels } = useStaticQuery(graphql`
    {
      faqPage {
        liferayFields {
          title
        }
        slug
      }
      allFaq: allLiferayFaqPost {
        nodes {
          liferayFields {
            title
            liferayId
          }
          faqcategory
          faqQuestion
          idVideoYoutube
        }
      }
      labelManagerLabels: labelManagerLabels {
        search
        hrfCategory
        washingMachineCategory
        tvCategory
        eletricWaterCategory
        refrigeratorCategory
        conditionersCategory
        noResults
      }
    }
  `);
  const categories = useMemo(
    () => [
      {
        label: labelManagerLabels.washingMachineCategory || "Washing Machine",
        value: "Washing Machine",
      },
      {
        label: labelManagerLabels.eletricWaterCategory || "Eletric Water Heaters",
        value: "Eletric Water Heaters",
      },
      {
        label: labelManagerLabels.tvCategory || "TV-Sets",
        value: "TV-Sets",
      },
      {
        label: labelManagerLabels.refrigeratorCategory || "Refrigerator",
        value: "Refrigerator",
      },
      {
        label: labelManagerLabels.conditionersCategory || "Conditioners",
        value: "Conditioners",
      },
    ],
    [labelManagerLabels]
  );
  const [category, setCategory] = useState(categories[0].value);
  const faqs = allFaq.nodes;
  const [searchText, setSearchText] = useState();
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
    setSearchText("");
  }, [category]);
  const filteredFaqs = useMemo(() => {
    const searchTerm = searchText?.trim()?.toLowerCase();
    const categoryFaqs = faqs.filter(
      (item) => item.faqQuestion && item.faqcategory?.[0] === category
    );
    return searchTerm
      ? categoryFaqs.filter((item) => item.faqQuestion?.toLowerCase()?.includes(searchTerm))
      : categoryFaqs;
  }, [faqs, category, searchText]);
  const totalPages = Math.ceil(filteredFaqs.length / faqperpage);
  const visibleFaqs = useMemo(
    () => filteredFaqs.slice(page * faqperpage, (page + 1) * faqperpage),
    [filteredFaqs, page, faqperpage]
  );

  const applySearch = () => {
    setPage(0);
    setSearchText(text);
  };

  const imageVideo = useMemo(() => getImage(data.imageVideo.node), [data.imageVideo]);
  const imageNoVideo = useMemo(() => getImage(data.imageNoVideo.node), [data.imageNoVideo]);

  if (!faqPage?.slug) return null;

  return (
    <section className="section faq">
      <div className="faq__search-box py-3">
        <div className="container">
          <div className="row">
            <div className="col-md-4 mb-2 mb-md-0">
              <label htmlFor="category" className="d-block">
                {labelManagerLabels.hrfCategory || "Categories"}
              </label>
              <select
                className="faq__select"
                onBlur={onBlur}
                id="category"
                onChange={(e) => setCategory(e.target.value)}
                value={category}
                name="category"
              >
                {categories.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-8">
              <label htmlFor="search-text" className="d-block">
                {labelManagerLabels.search}
              </label>
              <div className="faq__search d-flex align-items-center">
                <input
                  id="search-text"
                  onChange={(e) => setText(e.target.value)}
                  value={text}
                  maxLength="50"
                  className="faq__search-input mr-2"
                  name="search-text"
                  placeholder={labelManagerLabels.search}
                />
                <button
                  className="faq__search-btn btn-link"
                  onClick={applySearch}
                  aria-label={labelManagerLabels.search}
                >
                  <SearchIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="row my-4">
          {visibleFaqs.length ? (
            visibleFaqs.map((item) => (
              <div className="col-12 mb-4" key={item.liferayId}>
                <Link
                  className="faq__question d-inline-flex align-items-center"
                  to={normalizeRelativePath(safeSlugify(`${faqPage?.slug}/${item.title}`))}
                >
                  <GatsbyImage
                    className="flex-shrink-0"
                    image={item.idVideoYoutube ? imageVideo : imageNoVideo}
                    alt={
                      item.idVideoYoutube
                        ? imageVideo?.value?.description
                        : imageNoVideo?.value?.description
                    }
                  />
                  <span className="ml-2">{item.faqQuestion}</span>
                </Link>
              </div>
            ))
          ) : (
            <div className="faq__no-results p-3">
              {labelManagerLabels.noResults || "No result found!"}
            </div>
          )}
        </div>
      </div>
      {totalPages > 0 && (
        <div className="faq__pagination-wrapper">
          <div className="container">
            <div className="row">
              <div className="col-12 py-3 text-center">
                <div className="faq__pagination d-inline-flex align-items-center">
                  <button
                    className="faq__pagination-btn faq__pagination-btn--prev"
                    aria-label="Prev"
                    onClick={() => setPage((current) => current - 1)}
                    disabled={page === 0}
                  ></button>
                  {new Array(totalPages).fill(0).map((_, idx) => (
                    <React.Fragment key={idx}>
                      {idx === page ? (
                        <span className="faq__pagination-btn faq__pagination-btn--current d-inline-block mx-2">
                          {page + 1}
                        </span>
                      ) : (
                        <button className="faq__pagination-btn" onClick={() => setPage(idx)}>
                          {idx + 1}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                  <button
                    className="faq__pagination-btn faq__pagination-btn--next"
                    aria-label="Next"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={page >= totalPages - 1}
                  ></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default withPreview(FaqPage);
export const fragment = graphql`
  fragment FaqPageFragment on LiferayFaqPage {
    id
    liferayFields {
      siteId
      articleId
    }
    faqperpage
    imageVideo {
      node {
        gatsbyImageData(width: 50, layout: FIXED)
      }
      value {
        description
      }
    }
    imageNoVideo {
      node {
        gatsbyImageData(width: 50, layout: FIXED)
      }
      value {
        description
      }
    }
  }
`;
